import { createContext, useEffect, useReducer, useCallback, useMemo } from "react";
import useAuth from "app/hooks/useAuth";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";

const initialState = {
  notifications: [],
  loading: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };

    case "LOAD_NOTIFICATIONS":
      return {
        ...state,
        notifications: Array.isArray(action.payload) ? action.payload : [],
        loading: false,
      };

    case "MARK_READ": {
      const id = action.payload;
      const updated = state.notifications.map((n) =>
        n.UniqueID === id || n.id === id ? { ...n, IsRead: 1 } : n
      );
      return { ...state, notifications: updated };
    }

    case "MARK_UNREAD": {
      const id = action.payload;
      const updated = state.notifications.map((n) =>
        n.UniqueID === id || n.id === id ? { ...n, IsRead: 0 } : n
      );
      return { ...state, notifications: updated };
    }

    case "TOGGLE_PIN": {
      const id = action.payload;
      const updated = state.notifications
        .map((n) =>
          n.UniqueID === id || n.id === id
            ? { ...n, IsPinned: n.IsPinned ? 0 : 1 }
            : n
        )
        .sort((a, b) => (b.IsPinned || 0) - (a.IsPinned || 0));
      return { ...state, notifications: updated };
    }

    case "MARK_ALL_READ": {
      const updated = state.notifications.map((n) => ({ ...n, IsRead: 1 }));
      return { ...state, notifications: updated };
    }

    case "DELETE_NOTIFICATION": {
      const id = action.payload;
      const updated = state.notifications.filter(
        (n) => n.UniqueID !== id && n.id !== id
      );
      return { ...state, notifications: updated };
    }

    case "CLEAR_NOTIFICATIONS":
      return { ...state, notifications: [] };

    case "ADD_NOTIFICATION": {
      const newNotif = action.payload;
      const updated = [newNotif, ...state.notifications].sort(
        (a, b) => (b.IsPinned || 0) - (a.IsPinned || 0)
      );
      return { ...state, notifications: updated };
    }

    default:
      return state;
  }
};

const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  pinnedCount: 0,
  loading: false,
  getNotifications: () => { },
  markAsRead: () => { },
  markAsUnread: () => { },
  togglePin: () => { },
  markAllAsRead: () => { },
  deleteNotification: () => { },
  clearNotifications: () => { },
  createNotification: () => { },
});

export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { user } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const storageKey = useMemo(() => {
    return user?.UniqueID
      ? `lic_notifications_${user.UniqueID}`
      : "lic_notifications_guest";
  }, [user?.UniqueID]);

  // Load notifications from Backend API (with local cache fallback)
  const getNotifications = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });

    // Read cached local notifications first for instant render
    let cached = [];
    try {
      const localData = localStorage.getItem(storageKey);
      if (localData) {
        cached = JSON.parse(localData);
      }
    } catch (e) {
      console.warn("Error reading cached notifications:", e);
    }

    try {
      const response = await axiosPrivate.post("LoadNotifications.php", {
        UserID: user?.UniqueID || "ALL",
      });

      if (response.data?.status === 1 && Array.isArray(response.data?.Data)) {
        let serverData = response.data.Data;

        // If local had pin/read state modifications that need preservation
        if (cached.length > 0) {
          const localMap = new Map(cached.map((c) => [c.UniqueID || c.id, c]));
          serverData = serverData.map((s) => {
            const id = s.UniqueID || s.id;
            if (localMap.has(id)) {
              const loc = localMap.get(id);
              return {
                ...s,
                IsRead: loc.IsRead !== undefined ? loc.IsRead : s.IsRead,
                IsPinned: loc.IsPinned !== undefined ? loc.IsPinned : s.IsPinned,
              };
            }
            return s;
          });
        }

        // Sort: Pinned first, then by date descending
        serverData.sort((a, b) => {
          if ((b.IsPinned || 0) !== (a.IsPinned || 0)) {
            return (b.IsPinned || 0) - (a.IsPinned || 0);
          }
          return new Date(b.CreatedOn || 0) - new Date(a.CreatedOn || 0);
        });

        dispatch({ type: "LOAD_NOTIFICATIONS", payload: serverData });
        localStorage.setItem(storageKey, JSON.stringify(serverData));
        return;
      }
    } catch (err) {
      console.warn("Backend notification fetch notice (using local sync):", err?.message);
    }

    // Fallback: If server has no records yet or is unreachable, use cached or defaults
    if (cached.length > 0) {
      dispatch({ type: "LOAD_NOTIFICATIONS", payload: cached });
    } else {
      const defaults = [
        {
          UniqueID: `notif-welcome-${user?.UniqueID || "1"}`,
          Title: "Welcome to Dahiya Insurance WebApp",
          Message: `Hello ${user?.Name || "User"}, welcome to your centralized portal. System notices and alerts will appear in this notification bar.`,
          Type: "info",
          IsRead: 0,
          IsPinned: 1,
          CreatedOn: new Date().toISOString(),
        },
        {
          UniqueID: `notif-update-system`,
          Title: "System Performance Optimized",
          Message: "Follow-ups and celebrations have been organized into the main analytics dashboard for a cleaner notification workflow.",
          Type: "system",
          IsRead: 0,
          IsPinned: 0,
          CreatedOn: new Date(Date.now() - 3600000).toISOString(),
        },
      ];
      dispatch({ type: "LOAD_NOTIFICATIONS", payload: defaults });
      localStorage.setItem(storageKey, JSON.stringify(defaults));
    }
  }, [axiosPrivate, user?.UniqueID, user?.Name, storageKey]);

  // Sync state changes to localStorage
  useEffect(() => {
    if (state.notifications && state.notifications.length > 0) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(state.notifications));
      } catch (e) {
        console.warn("Error saving notifications to localStorage:", e);
      }
    }
  }, [state.notifications, storageKey]);

  // Initial load
  useEffect(() => {
    getNotifications();
  }, [getNotifications]);

  // Action: Mark as Read
  const markAsRead = useCallback(
    async (notificationID) => {
      dispatch({ type: "MARK_READ", payload: notificationID });
      try {
        await axiosPrivate.post("UpdateNotificationStatus.php", {
          Action: "markRead",
          NotificationID: notificationID,
          UserID: user?.UniqueID || "ALL",
        });
      } catch (e) {
        // Local state already updated
      }
    },
    [axiosPrivate, user?.UniqueID]
  );

  // Action: Mark as Unread
  const markAsUnread = useCallback(
    async (notificationID) => {
      dispatch({ type: "MARK_UNREAD", payload: notificationID });
      try {
        await axiosPrivate.post("UpdateNotificationStatus.php", {
          Action: "markUnread",
          NotificationID: notificationID,
          UserID: user?.UniqueID || "ALL",
        });
      } catch (e) {
        // Local state already updated
      }
    },
    [axiosPrivate, user?.UniqueID]
  );

  // Action: Toggle Pin Status
  const togglePin = useCallback(
    async (notificationID) => {
      const item = state.notifications.find(
        (n) => n.UniqueID === notificationID || n.id === notificationID
      );
      const newPinState = item && item.IsPinned ? 0 : 1;
      dispatch({ type: "TOGGLE_PIN", payload: notificationID });

      try {
        await axiosPrivate.post("UpdateNotificationStatus.php", {
          Action: "togglePin",
          NotificationID: notificationID,
          IsPinned: newPinState,
          UserID: user?.UniqueID || "ALL",
        });
      } catch (e) {
        // Local state already updated
      }
    },
    [axiosPrivate, state.notifications, user?.UniqueID]
  );

  // Action: Mark All as Read
  const markAllAsRead = useCallback(async () => {
    dispatch({ type: "MARK_ALL_READ" });
    try {
      await axiosPrivate.post("UpdateNotificationStatus.php", {
        Action: "markAllRead",
        UserID: user?.UniqueID || "ALL",
      });
    } catch (e) {
      // Local state already updated
    }
  }, [axiosPrivate, user?.UniqueID]);

  // Action: Delete a Notification
  const deleteNotification = useCallback(
    async (notificationID) => {
      dispatch({ type: "DELETE_NOTIFICATION", payload: notificationID });
      try {
        await axiosPrivate.post("UpdateNotificationStatus.php", {
          Action: "delete",
          NotificationID: notificationID,
          UserID: user?.UniqueID || "ALL",
        });
      } catch (e) {
        // Local state already updated
      }
    },
    [axiosPrivate, user?.UniqueID]
  );

  // Action: Clear All
  const clearNotifications = useCallback(() => {
    dispatch({ type: "CLEAR_NOTIFICATIONS" });
    try {
      localStorage.removeItem(storageKey);
    } catch (e) { }
  }, [storageKey]);

  // Action: Create / Add New Notification
  const createNotification = useCallback(
    (notification) => {
      const newNotif = {
        UniqueID: notification.UniqueID || `notif-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        UserID: notification.UserID || user?.UniqueID || "ALL",
        Title: notification.Title || notification.title || "New Notification",
        Message: notification.Message || notification.message || notification.subtitle || "",
        Type: notification.Type || notification.type || "info",
        IsRead: 0,
        IsPinned: notification.IsPinned ? 1 : 0,
        Link: notification.Link || notification.link || null,
        CreatedOn: notification.CreatedOn || new Date().toISOString(),
      };
      dispatch({ type: "ADD_NOTIFICATION", payload: newNotif });
    },
    [user?.UniqueID]
  );

  // Derived counts
  const unreadCount = useMemo(() => {
    return state.notifications.filter((n) => !n.IsRead).length;
  }, [state.notifications]);

  const pinnedCount = useMemo(() => {
    return state.notifications.filter((n) => Boolean(n.IsPinned)).length;
  }, [state.notifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications: state.notifications,
        unreadCount,
        pinnedCount,
        loading: state.loading,
        getNotifications,
        markAsRead,
        markAsUnread,
        togglePin,
        markAllAsRead,
        deleteNotification,
        clearNotifications,
        createNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
